import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, shareReplay, takeUntil, filter } from 'rxjs/operators';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { SuperloginService } from '../superlogin/superlogin.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('paginator') paginator: MatPaginator;

  currentUserRole: string | null = null;
  currentUser: any = null;
  userFullName: string = '';
  currentRoute: string = '';
  
  public loading = false;


    public loaderConfig = {
    animationType: 'circleSwish',
    backdropBorderRadius: '2px',
    primaryColour: '#630f12',
    secondaryColour: '#630f12',
    tertiaryColour: '#ffcb05',
    backdropBackgroundColour: 'rgba(0, 0, 0, 0.5)',
    fullScreenBackdrop: true
  };

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );

  private readonly roleDashboardRoutes: { [key: string]: string } = {
    'SYSADMIN': 'sysadmin-dashboard',
    'QA': 'qa-dashboard',
    'ADMIN': 'admin-dashboard',
    'MANAGEMENT': 'management-dashboard',
    'REGISTRAR': 'registrar-dashboard',
    'HOD': 'lecturer-dashboard',
    'DEAN': 'lecturer-dashboard',
    'LECTURER': 'lecturer-dashboard',
    'EXAM_OFFICER': 'exam-officer-dashboard',
    'ACCOUNTANT': 'accountant-dashboard',
    'ESTMANAGER': 'estate-officer',
    'LIBRARIAN': 'librarian-dashboard',
    'FOUNDER':'Founder'
  };

  constructor(
    public router: Router,
    private breakpointObserver: BreakpointObserver,
    private superloginService: SuperloginService
  ) {
    this.setupRouteLoading();
  }

  ngOnInit(): void {
    this.loading = true;
    this.initializeUserData();
    this.trackRouteChanges();
    this.redirectToRoleDashboard();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  
  private setupRouteLoading(): void {
    this.router.events.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loading = true;
        console.log('Navigation started, showing loader...');
      }
      
      if (event instanceof NavigationEnd) {
        setTimeout(() => {
          this.loading = false;
          console.log('Navigation ended, hiding loader...');
        }, 2000);
      }

      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.loading = false;
        console.log('Navigation cancelled/error, hiding loader...');
      }
    });
  }

  private initializeUserData(): void {
    this.currentUser = this.superloginService.getCurrentUser();
    this.currentUserRole = this.superloginService.getUserRole();

    console.log('=== DASHBOARD DEBUG ===');
    console.log('Raw user role:', this.currentUserRole);

    if (this.currentUserRole) {
      this.currentUserRole = this.currentUserRole.toUpperCase();
      console.log('Uppercased role:', this.currentUserRole);
    }

    this.userFullName = this.getUserDisplayName();

    console.log('Final role:', this.currentUserRole);
    console.log('Dashboard title:', this.getDashboardTitle(this.currentUserRole));
    console.log('======================');
  }

  private redirectToRoleDashboard(): void {
    const currentUrl = this.router.url;
    
    if (currentUrl === '/dashboard') {
      if (this.currentUserRole && this.roleDashboardRoutes[this.currentUserRole]) {
        const targetRoute = this.roleDashboardRoutes[this.currentUserRole];
        console.log(`Redirecting role ${this.currentUserRole} to /dashboard/${targetRoute}`);
        this.router.navigateByUrl(`/dashboard/${targetRoute}`, { replaceUrl: true });
      } else {
        console.warn(`User role ${this.currentUserRole} is unknown or has no defined dashboard route. Showing default welcome.`);
      }
    } else {
      console.log(`Not redirecting. Already on a child route: ${currentUrl}`);
    }
  }


private trackRouteChanges(): void {
  this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    )
    .subscribe((event: NavigationEnd) => {
      this.currentRoute = event.urlAfterRedirects;
      
      if (this.currentRoute === '/dashboard') {
        this.redirectToRoleDashboard();
      }
      
      console.log('Current URL:', event.url);
    });
}

  private getUserDisplayName(): string {
    if (!this.currentUser) return 'User';
    const firstName = this.currentUser.firstName || this.currentUser.first_name || '';
    const lastName = this.currentUser.lastName || this.currentUser.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || this.currentUser.email || this.currentUser.username || 'User';
  }

  getDashboardTitle(role: string | null): string {
    if (!role) return 'Management Portal Dashboard';
    
    const roleTitles: { [key: string]: string } = {
      'SYSADMIN': 'System Administrator Dashboard',
      'ADMIN': 'Administrator Dashboard',
      'MANAGEMENT': 'Executive Management Dashboard',
      'REGISTRAR': 'Registrar Dashboard',
      'HOD': 'Head of Department Dashboard',
      'DEAN': 'Dean Dashboard',
      'LECTURER': 'Lecturer Dashboard',
      'EXAM_OFFICER': 'Examination Officer Dashboard',
      'ACCOUNTANT': 'Finance Dashboard',
    'ESTMANAGER': 'Estate-officer',
      'QA': 'Quality Assurance Dashboard',
      'LIBRARIAN': 'Library Services Dashboard',
      'FOUNDER':'Founder'
    };

    if (this.router.url.includes('sysadmin-dashboard')) return roleTitles['SYSADMIN'];
    if (this.router.url.includes('qa-dashboard')) return roleTitles['QA'];

    return roleTitles[role] || 'Management Portal Dashboard';
  }

  isUnknownRole(role: string | null): boolean {
    if (!role) return true;
    
    const knownRoles = Object.keys(this.roleDashboardRoutes);
    knownRoles.push('STUDENT', 'VICE_CHANCELLOR');

    return !knownRoles.includes(role);
  }

  hasAccess(requiredRoles: string[]): boolean {
    return this.currentUserRole ? requiredRoles.includes(this.currentUserRole) : false;
  }

  getRoleDisplayName(role: string): string {
    const roleNames: { [key: string]: string } = {
      SYSADMIN: 'System Administrator',
      ACCOUNTANT: 'Accountant',
      MANAGEMENT: 'Management',
      REGISTRAR: 'Registrar',
      ADMIN: 'Administrator',
      QA: 'Quality Assurance',
      HOD: 'Head of Department',
      DEAN: 'Dean',
      LECTURER: 'Lecturer',
      EXAM_OFFICER: 'Exam Officer',
      ESTMANAGER: 'Estate Officer',
      LIBRARIAN: 'Librarian',
      FOUNDER: 'Founder',

    };
    const normalizedRole = role ? role.toUpperCase() : '';
    return roleNames[normalizedRole] || role;
  }

  testLoginService() {
    console.log('=== LOGIN SERVICE TEST (Refreshed Data) ===');
    console.log('getCurrentUser():', this.superloginService.getCurrentUser());
    console.log('getUserRole():', this.superloginService.getUserRole());
    console.log('isAuthenticated():', this.superloginService.isAuthenticated());
    this.initializeUserData();
    console.log('========================');
  }
}